import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faCircleCheck, faRoute} from "@fortawesome/free-solid-svg-icons";
import {
  useResourcePersonCourses,
  useResourcePersonLearningPaths,
  useResourcePersonLessons,
} from "../store/resourcePerson/resourcePersonStore";
import ResourcePersonShell from "../components/resourcePerson/ResourcePersonShell";
import Toast from "../components/Toast";
import LearningPathDetailsStep from "./learningPath/LearningPathDetailsStep";
import LearningPathCoursesStep from "./learningPath/LearningPathCoursesStep";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";

/**
 * Resource-person learning-path builder — a 2-step wizard that mirrors the
 * course builder (ResourcePersonCourseBuilder) so both flows read the same:
 *
 *   Step 1 · Path details  POST /api/resource-person/learning-paths
 *   Step 2 · Courses       POST   /learning-paths/:id/courses  { courseId, position? }
 *                          PATCH  /learning-paths/:id/courses/reorder  { courseIds }
 *                          DELETE /learning-paths/:id/courses/:courseId
 *                          PATCH  /learning-paths/:id/submit | …/unsubmit
 *                          GET    /learning-paths/:id/analytics
 *
 * Step 1 is the only gated step: saving a brand-new path creates the shell,
 * auto-advances to step 2 and re-routes to the persistent /build URL.
 */
const ResourcePersonLearningPathBuilder = () => {
  const {id} = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const confirm = useConfirmDialog();

  const {courses, fetchCourses} = useResourcePersonCourses();
  const {fetchLessonCount, fetchModuleCount} = useResourcePersonLessons();
  const {
    learningPathDetail,
    learningPathAnalytics,
    learningPathMutationLoading,
    learningPathMutationError,
    fetchLearningPathById,
    createLearningPath,
    updateLearningPath,
    deleteLearningPath,
    addCourseToPath,
    removeCourseFromPath,
    reorderPathCourses,
    submitLearningPath,
    unsubmitLearningPath,
    fetchLearningPathAnalytics,
  } = useResourcePersonLearningPaths();

  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({title: "", description: "", coverImage: "", price: ""});
  // Non-null once a brand-new path's draft exists — drives `hasShell`.
  const [savedShellId, setSavedShellId] = useState(null);
  const [addCourseId, setAddCourseId] = useState("");
  const [addPosition, setAddPosition] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [lessonCounts, setLessonCounts] = useState({});
  const [moduleCounts, setModuleCounts] = useState({});
  const [lessonCountLoading, setLessonCountLoading] = useState(false);
  const [lessonCountErrors, setLessonCountErrors] = useState([]);
  // Step 1 for a new path, step 2 for an existing one (its shell is saved).
  const [step, setStep] = useState(isNew ? 1 : 2);

  const fail = (message) => setToast({text: String(message || "Request failed"), ok: false});
  const ok = (text) => setToast({text, ok: true});

  const detail = learningPathDetail || {};
  const status = String(detail.status || (isNew ? "DRAFT" : "")).toUpperCase();
  const pathCourses = detail.courses || detail.items || [];
  const pathCourseId = (course) => course.courseId || course.course?.id || course.id;
  const draftCourses = (courses || []).filter(
    (course) => String(course.status || "").toUpperCase() === "DRAFT"
  );
  const eligibleCourses = draftCourses.filter((course) => {
    const courseId = course.id || course._id;
    return Number(lessonCounts[courseId]) > 0;
  });
  const canSubmitPath =
    pathCourses.length > 0 &&
    pathCourses.every((pathCourse) => {
      const courseId = pathCourseId(pathCourse);
      const course = draftCourses.find((item) => String(item.id || item._id) === String(courseId));
      return Boolean(course && Number(lessonCounts[courseId]) > 0);
    });
  const hasShell = isNew ? Boolean(savedShellId) : true;
  const locked = !isNew && status !== "DRAFT";

  useEffect(() => {
    fetchCourses({limit: 100});
  }, [fetchCourses]);

  useEffect(() => {
    let cancelled = false;
    const loadLessonCounts = async () => {
      setLessonCounts({});
      setModuleCounts({});
      setLessonCountErrors([]);
      const counts = {};
      const modules = {};
      const errors = [];
      setLessonCountLoading(draftCourses.length > 0);

      for (const course of draftCourses) {
        if (cancelled) return;
        const embeddedCount =
          course.lessonCount ??
          course.lessonsCount ??
          (Array.isArray(course.lessons) ? course.lessons.length : undefined) ??
          (Array.isArray(course.modules) && course.modules.every((module) => Array.isArray(module.lessons))
            ? course.modules.reduce((total, module) => total + module.lessons.length, 0)
            : undefined);
        const embeddedModuleCount =
          course.moduleCount ??
          course.modulesCount ??
          (Array.isArray(course.modules) ? course.modules.length : undefined);
        if (embeddedCount !== undefined) {
          counts[course.id || course._id] = Number(embeddedCount);
        }
        if (embeddedModuleCount !== undefined) {
          modules[course.id || course._id] = Number(embeddedModuleCount);
        }
        if (embeddedCount === undefined || embeddedModuleCount === undefined) {
          const courseId = course.id || course._id;
          const [lessonResult, moduleResult] = await Promise.all([
            embeddedCount === undefined ? fetchLessonCount(courseId) : Promise.resolve(null),
            embeddedModuleCount === undefined ? fetchModuleCount(courseId) : Promise.resolve(null),
          ]);
          if (lessonResult) {
            if (!lessonResult.success) {
              errors.push(`${course.title || courseId} lessons: ${lessonResult.error}`);
            } else {
              counts[courseId] = lessonResult.count;
            }
          }
          if (moduleResult) {
            if (!moduleResult.success) {
              errors.push(`${course.title || courseId} modules: ${moduleResult.error}`);
            } else {
              modules[courseId] = moduleResult.count;
            }
          }
        }
      }

      if (!cancelled) {
        setLessonCounts(counts);
        setModuleCounts(modules);
        setLessonCountErrors(errors);
        setLessonCountLoading(false);
      }
    };

    loadLessonCounts();
    return () => {
      cancelled = true;
    };
  }, [courses, fetchLessonCount, fetchModuleCount]);

  useEffect(() => {
    if (!isNew) fetchLearningPathById(id);
  }, [id, isNew, fetchLearningPathById]);

  useEffect(() => {
    if (isNew || !learningPathDetail) return;
    setForm({
      title: detail.title || "",
      description: detail.description || "",
      coverImage: detail.coverImage || detail.cover_image || "",
      price: detail.price ?? "",
    });
  }, [isNew, learningPathDetail]);

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return fail("Give the learning path a title.");
    const payload = {
      title: form.title.trim(),
      ...(form.description ? {description: form.description} : {}),
      ...(form.coverImage ? {coverImage: form.coverImage} : {}),
      ...(form.price !== "" ? {price: Number(form.price)} : {}),
    };
    const result = isNew ? await createLearningPath(payload) : await updateLearningPath(id, payload);
    if (!result.success) return fail(result.error);
    ok(isNew ? "Draft created — now add a draft course with at least one lesson." : "Learning path updated.");
    if (!isNew) return;
    // Auto-advance to the course step, then re-route to the persistent URL so a
    // refresh keeps the author on step 2 with the shell already saved.
    const newId = result.learningPath?.id || result.learningPath?._id;
    if (!newId) return;
    setSavedShellId(newId);
    setStep(2);
    navigate(`/resource-person/content-library/path/${newId}/build`, {replace: true});
  };

  const addCourse = async (event) => {
    event.preventDefault();
    if (!addCourseId) return fail("Choose one of your draft courses.");
    const result = await addCourseToPath(
      id,
      addCourseId,
      addPosition === "" ? undefined : Number(addPosition)
    );
    if (!result.success) return fail(result.error);
    setAddCourseId("");
    setAddPosition("");
    fetchLearningPathById(id);
    ok("Course added to the path.");
  };

  const moveCourse = async (index, direction) => {
    const ordered = [...pathCourses];
    const target = index + direction;
    if (target < 0 || target >= ordered.length) return;
    const [moved] = ordered.splice(index, 1);
    ordered.splice(target, 0, moved);
    const result = await reorderPathCourses(id, ordered.map(pathCourseId));
    if (!result.success) return fail(result.error);
    fetchLearningPathById(id);
    ok("Path order saved.");
  };

  const removeCourse = async (course) => {
    const result = await removeCourseFromPath(id, pathCourseId(course));
    if (!result.success) return fail(result.error);
    fetchLearningPathById(id);
    ok("Course removed.");
  };

  const fieldClass =
    "w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-300 disabled:bg-slate-100";
  const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";
  const primaryBtn =
    "inline-flex items-center gap-2 rounded-md bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60";
  const ghostBtn =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60";

  const STEPS = [
    {n: 1, label: "Path details", hint: hasShell ? "Saved" : "Create the draft"},
    {n: 2, label: "Course sequence", hint: pathCourses.length ? `${pathCourses.length} added` : "Add at least one"},
  ];

  const stepProps = {
    isNew,
    locked,
    status,
    pending: learningPathMutationLoading,
    fieldClass,
    labelClass,
    primaryBtn,
    ghostBtn,
  };

  const submit = async () => {
    if (!canSubmitPath) {
      return fail("A learning path can only be submitted with draft courses that each have at least one lesson.");
    }
    const result = await submitLearningPath(id);
    if (!result.success) return fail(result.error);
    await fetchLearningPathById(id);
    ok("Path submitted for review.");
  };

  const unsubmit = async () => {
    const result = await unsubmitLearningPath(id);
    if (!result.success) return fail(result.error);
    await fetchLearningPathById(id);
    ok("Path withdrawn from review.");
  };

  return (
    <ResourcePersonShell
      title={isNew ? "New learning path" : form.title || "Learning path"}
      subtitle="Chain courses together; each course contains its own modules and lessons"
      icon={faRoute}
      actions={
        <button type="button" className={ghostBtn} onClick={() => navigate("/resource-person/content-library")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Library
        </button>
      }
    >
      {toast ? <Toast message={toast.text} onClose={() => setToast(null)} isSuccess={toast.ok} /> : null}
      {learningPathMutationError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{learningPathMutationError}</p>
      ) : null}

      {/* Stepper — same pattern as the course builder: complete/current/locked. */}
      <nav
        aria-label="Learning path builder steps"
        className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
      >
        <ol className="m-0 flex list-none flex-col gap-3 p-0 sm:flex-row sm:items-center">
          {STEPS.map((item, index) => {
            const complete = item.n === 1 ? hasShell : pathCourses.length > 0;
            const current = step === item.n;
            return (
              <React.Fragment key={item.n}>
                <li className="flex min-w-0 items-center">
                  <button
                    type="button"
                    onClick={() => setStep(item.n)}
                    aria-current={current ? "step" : undefined}
                    className={`flex min-w-0 items-center gap-3 rounded-md px-1 py-0.5 text-left ${
                      hasShell || item.n === 1 ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                    }`}
                    disabled={item.n === 2 && !hasShell}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        complete
                          ? "bg-emerald-600 text-white"
                          : current
                          ? "bg-blue-900 text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {complete && !current ? <FontAwesomeIcon icon={faCircleCheck} /> : item.n}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-slate-900">{item.label}</span>
                      <span className="block truncate text-xs text-slate-500">{item.hint}</span>
                    </span>
                  </button>
                </li>
                {index < STEPS.length - 1 ? (
                  <li
                    aria-hidden="true"
                    className={`hidden h-0.5 flex-1 rounded-full sm:block ${
                      complete && hasShell ? "bg-emerald-600" : "bg-slate-200"
                    }`}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>

      {step === 1 ? (
        <LearningPathDetailsStep
          {...stepProps}
          form={form}
          setForm={setForm}
          hasShell={hasShell}
          showAnalytics={showAnalytics}
          analytics={learningPathAnalytics}
          onSave={save}
          onContinue={() => setStep(2)}
          onSubmit={submit}
          onUnsubmit={unsubmit}
          onDelete={async () => {
            return confirm({
              title: "Delete this learning path draft?",
              description: "This action cannot be undone.",
              confirmLabel: "Delete learning path",
              onConfirm: async () => {
                const result = await deleteLearningPath(id);
                if (!result.success) {
                  fail(result.error);
                  throw new Error(result.error || "Could not delete learning path.");
                }
                navigate("/resource-person/content-library", {replace: true});
              },
            });
          }}
          onToggleAnalytics={async () => {
            const next = !showAnalytics;
            setShowAnalytics(next);
            if (next) {
              const result = await fetchLearningPathAnalytics(id);
              if (!result.success) fail(result.error);
            }
          }}
        />
      ) : (
        <LearningPathCoursesStep
          {...stepProps}
          pathCourses={pathCourses}
          eligibleCourses={eligibleCourses}
          lessonCounts={lessonCounts}
          moduleCounts={moduleCounts}
          eligibleCoursesLoading={lessonCountLoading}
          eligibleCoursesError={lessonCountErrors.join("; ")}
          canSubmit={canSubmitPath}
          addCourseId={addCourseId}
          setAddCourseId={setAddCourseId}
          addPosition={addPosition}
          setAddPosition={setAddPosition}
          onAddCourse={addCourse}
          onMoveCourse={moveCourse}
          onRemoveCourse={removeCourse}
          onBackToDetails={() => setStep(1)}
          onSubmit={submit}
          onUnsubmit={unsubmit}
          onCreateCourse={() => navigate(`/resource-person/content-library/path/${id}/course/new`)}
          onBuildCourse={(course) =>
            navigate(`/resource-person/content-library/path/${id}/course/${course.courseId || course.course?.id || course.id}`)
          }
        />
      )}
    </ResourcePersonShell>
  );
};

export default ResourcePersonLearningPathBuilder;
